import React, { useState, useEffect } from 'react';
import { db } from '../firebaseConfig';
import { collection, query, where, onSnapshot, orderBy } from 'firebase/firestore';
import { Calendar, Play, Award, Film, Download } from 'lucide-react';
import { turkeyCities as TURKEY_CITIES } from '../data/turkeyCities';

const MatchArchives = () => {
    const [selectedCity, setSelectedCity] = useState("");
    const [selectedDistrict, setSelectedDistrict] = useState("");
    const [selectedField, setSelectedField] = useState("");
    const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
    const [fields, setFields] = useState([]);
    const [archives, setArchives] = useState([]);
    const [activeVideo, setActiveVideo] = useState(null);
    const [loading, setLoading] = useState(true);

    // 1. Onaylı sahaları çek
    useEffect(() => {
        const q = query(collection(db, "tesisler"), where("status", "==", "approved"));
        const unsub = onSnapshot(q, (snapshot) => {
            const dbFields = snapshot.docs.map(doc => ({
                id: doc.id,
                fieldName: doc.data().fieldName || doc.data().name || "Saha",
                city: doc.data().city || "",
                district: doc.data().district || ""
            }));
            setFields(dbFields);
        });
        return () => unsub();
    }, []);

    // 2. macKayitlari koleksiyonundan gerçek kayıtları çek
    useEffect(() => {
        setLoading(true);
        let q = query(collection(db, "macKayitlari"), orderBy("createdAt", "desc"));

        const unsub = onSnapshot(q, (snapshot) => {
            const all = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

            // Filtreleri uygula
            const filtered = all.filter(item => {
                const matchField = !selectedField || item.fieldId === selectedField;
                const matchDate = !selectedDate || item.date === selectedDate;
                const matchCity = !selectedCity || (item.city || "").toLowerCase() === selectedCity.toLowerCase();
                const matchDistrict = !selectedDistrict || (item.district || "").toLowerCase() === selectedDistrict.toLowerCase();
                return matchField && matchDate && matchCity && matchDistrict;
            });

            setArchives(filtered);
            setActiveVideo(filtered.length > 0 ? filtered[0] : null);
            setLoading(false);
        }, () => setLoading(false));

        return () => unsub();
    }, [selectedField, selectedDate, selectedCity, selectedDistrict]);

    const filteredFields = fields.filter(f => {
        const cityMatch = !selectedCity || f.city.toLowerCase() === selectedCity.toLowerCase();
        const distMatch = !selectedDistrict || f.district.toLowerCase() === selectedDistrict.toLowerCase();
        return cityMatch && distMatch;
    });

    return (
        <div className="min-vh-100 bg-light py-5">
            <style>
                {`
                    .archive-card {
                        background: #ffffff;
                        border-radius: 20px;
                        overflow: hidden;
                        box-shadow: 0 10px 25px rgba(0,0,0,0.05);
                        border: 1px solid #f1f5f9;
                    }
                    .archive-item {
                        border-radius: 12px;
                        transition: all 0.2s ease;
                        cursor: pointer;
                    }
                    .archive-item:hover {
                        background: #f8fafc;
                        transform: translateX(4px);
                    }
                    .archive-item.active {
                        background: #e2e8f0;
                        border-left: 4px solid #0f172a;
                    }
                    .video-wrapper {
                        background: #000;
                        border-radius: 16px;
                        overflow: hidden;
                        position: relative;
                        padding-top: 56.25%;
                    }
                    .video-wrapper video {
                        position: absolute;
                        top: 0;
                        left: 0;
                        width: 100%;
                        height: 100%;
                        object-fit: contain;
                    }
                `}
            </style>

            <div className="container">
                {/* Sayfa Başlığı */}
                <div className="mb-5 text-center">
                    <h1 className="fw-bold text-dark d-flex align-items-center justify-content-center gap-2">
                        <Film className="text-danger" size={32} /> Geçmiş Maç Yayınları
                    </h1>
                    <p className="text-muted">Maçını tamamladıktan sonra buraya gelerek kaydını geriye dönük izle ve indir.</p>
                </div>

                {/* Filtreleme Kartı */}
                <div className="card border-0 shadow-sm p-4 mb-5 rounded-4 bg-white">
                    <div className="row g-3">
                        <div className="col-md-3">
                            <label className="fw-bold small mb-1 ms-1 text-muted">Şehir</label>
                            <select className="form-select border-0 bg-light p-3 shadow-none rounded-3" value={selectedCity} onChange={(e) => { setSelectedCity(e.target.value); setSelectedDistrict(""); setSelectedField(""); }}>
                                <option value="">Tüm Şehirler</option>
                                {Object.keys(TURKEY_CITIES).map(c => <option key={c} value={c}>{c}</option>)}
                            </select>
                        </div>
                        <div className="col-md-3">
                            <label className="fw-bold small mb-1 ms-1 text-muted">İlçe</label>
                            <select className="form-select border-0 bg-light p-3 shadow-none rounded-3" value={selectedDistrict} onChange={(e) => { setSelectedDistrict(e.target.value); setSelectedField(""); }} disabled={!selectedCity}>
                                <option value="">Tüm İlçeler</option>
                                {selectedCity && TURKEY_CITIES[selectedCity].map(d => <option key={d} value={d}>{d}</option>)}
                            </select>
                        </div>
                        <div className="col-md-3">
                            <label className="fw-bold small mb-1 ms-1 text-muted">Saha</label>
                            <select className="form-select border-0 bg-light p-3 shadow-none rounded-3" value={selectedField} onChange={(e) => setSelectedField(e.target.value)}>
                                <option value="">Tüm Sahalar</option>
                                {filteredFields.map(f => <option key={f.id} value={f.id}>{f.fieldName}</option>)}
                            </select>
                        </div>
                        <div className="col-md-3">
                            <label className="fw-bold small mb-1 ms-1 text-muted">Tarih</label>
                            <input type="date" className="form-control border-0 bg-light p-3 shadow-none rounded-3" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} />
                        </div>
                    </div>
                </div>

                {/* Ana İçerik */}
                <div className="row g-4">
                    {/* Oynatıcı Alanı */}
                    <div className="col-lg-8">
                        <div className="archive-card p-4">
                            {activeVideo ? (
                                <div>
                                    <div className="video-wrapper mb-4">
                                        <video key={activeVideo.id} src={activeVideo.videoUrl} controls autoPlay />
                                    </div>
                                    <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
                                        <div>
                                            <h4 className="fw-bold text-dark mb-1">{activeVideo.fieldName}</h4>
                                            <p className="text-muted mb-0">
                                                <Calendar className="me-1 d-inline-block" size={16} /> {activeVideo.date} | <b>{activeVideo.timeSlot}</b>
                                            </p>
                                        </div>
                                        <div className="d-flex gap-2">
                                            <a href={activeVideo.videoUrl} download target="_blank" rel="noopener noreferrer" className="btn btn-outline-dark rounded-pill px-3 py-2 fw-bold d-flex align-items-center gap-2">
                                                <Download size={16} /> İndir
                                            </a>
                                            <span className="badge bg-danger bg-opacity-10 text-danger border border-danger border-opacity-25 fs-6 py-2 px-3 rounded-pill d-flex align-items-center gap-1">
                                                <Award size={18} /> Skor: {activeVideo.score}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                ) : (
                                    <div className="text-center py-5">
                                        <Film size={64} className="text-secondary opacity-25 mb-3" />
                                        <h5 className="fw-bold text-muted">{loading ? 'Yükleniyor...' : 'Kayıt Bulunamadı'}</h5>
                                        <p className="text-muted small">{loading ? '' : 'Seçili filtreler için kayıtlı bir maç videosu bulunamadı. Farklı tarih veya saha seçin.'}</p>
                                    </div>
                                )}
                        </div>
                    </div>

                    {/* Liste Alanı */}
                    <div className="col-lg-4">
                        <div className="archive-card p-4 h-100" style={{ maxHeight: '600px', overflowY: 'auto' }}>
                            <h5 className="fw-bold text-dark mb-4"><Film className="text-danger" size={18} /> Maç Kayıtları ({archives.length})</h5>
                            <div className="d-flex flex-column gap-3">
                                {archives.map((item) => (
                                    <div
                                        key={item.id}
                                        onClick={() => setActiveVideo(item)}
                                        className={`archive-item p-3 border ${activeVideo?.id === item.id ? 'active' : ''}`}
                                    >
                                        <div className="d-flex justify-content-between align-items-start">
                                            <div>
                                                <h6 className="fw-bold mb-1 text-dark">{item.timeSlot}</h6>
                                                <small className="text-muted d-block mb-1">{item.fieldName}</small>
                                                <span className="badge bg-light text-muted small">{item.duration}</span>
                                            </div>
                                            <Play size={20} className={activeVideo?.id === item.id ? 'text-primary' : 'text-secondary opacity-50'} />
                                        </div>
                                    </div>
                                ))}
                                {archives.length === 0 && !loading && (
                                    <div className="text-center py-5 text-muted">
                                        <Film size={40} className="opacity-25 mb-2" />
                                        <p className="small">Henüz bu kriterlere uygun kayıt yok.<br/>Tesis sahipleri Admin Paneli → Maç Kayıtları'ndan video yükleyebilir.</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MatchArchives;
